import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SOCIAL_PROVIDERS, providerMeta, matchesProviderDomain } from "@/lib/social";
import { Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";

const isHttps = (u) => { try { return new URL(String(u).trim()).protocol === "https:"; } catch { return false; } };

// Global Settings → Social Media Links. Single source of truth = config.social_links.
export function SocialLinksEditor({ links, onChange }) {
  const list = links || [];
  const update = (i, patch) => onChange(list.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...list, { provider: "instagram", url: "", active: true, order: list.length }]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i).map((l, k) => ({ ...l, order: k })));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((l, k) => ({ ...l, order: k })));
  };

  return (
    <div className="mt-8 rounded-lg border border-border bg-cream/30 p-4" data-testid="social-links-editor">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-accent text-sm font-semibold text-charcoal">Social Media Links</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">Shown in the footer. Icons are chosen automatically per provider; URLs are validated against the provider's domains.</p>
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid="social-add" className="rounded-full text-xs"><Plus className="mr-1 h-3.5 w-3.5" />Add link</Button>
      </div>
      {list.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="social-empty">No social links yet. Add one to display icons in the footer.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {list.map((l, i) => {
            const meta = providerMeta(l.provider);
            const Icon = meta.Icon;
            const badUrl = (l.url || "").trim() && (!isHttps(l.url) || !matchesProviderDomain(l.provider, l.url));
            return (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-white p-2" data-testid={`social-row-${i}`}>
                <Icon className="h-4 w-4 shrink-0 text-saffron" />
                <Select value={l.provider} onValueChange={(v) => update(i, { provider: v })}>
                  <SelectTrigger className="h-9 w-40" data-testid={`social-provider-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{SOCIAL_PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-9 min-w-[220px] flex-1" placeholder={meta.domains ? `https://${meta.domains[0]}/…` : "https://…"} value={l.url || ""} onChange={(e) => update(i, { url: e.target.value })} data-testid={`social-url-${i}`} />
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={l.active !== false} onChange={(e) => update(i, { active: e.target.checked })} data-testid={`social-active-${i}`} /> Active</label>
                <div className="flex items-center">
                  <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`social-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`social-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`social-remove-${i}`}><Trash2 className="h-4 w-4 text-maroon" /></Button>
                </div>
                {badUrl && <div className="flex w-full items-center gap-1 text-xs text-maroon" data-testid={`social-invalid-${i}`}><AlertTriangle className="h-3 w-3" />URL must be a valid https:// {meta.label} link{meta.domains ? ` (${meta.domains.join(", ")})` : ""}.</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
